import React from "react";
import {
  ArrowLeft,
  Download,
  FileText,
  Box,
  ShieldCheck,
  ScrollText,
  Mail,
  Sparkles,
  Package,
} from "lucide-react";
import { toast } from "sonner";

/**
 * V2.0.28 — Project Delivery Dashboard.
 *
 * Premium delivery screen rendered after `handleBuildPackage` succeeds.
 * Replaces the silent "click → file lands in Downloads/" pattern with a
 * "wow, look what I just bought" moment that mirrors the unboxing
 * conventions of high-end design tools (Adobe project exports, Apple).
 *
 * Props:
 *   designName       — string (used in the hero and the mailto subject)
 *   designId         — string (deep-linked back in the mailto body)
 *   filename         — string from API (e.g. "Grand_Colonial-architect-package.zip")
 *   zipBlobUrl       — string (blob: URL of the assembled zip)
 *   sizeBytes        — number (total package size)
 *   contents         — { floor_plan_pdf_bytes, ifc_bytes, acceptance_log_pdf_bytes, acceptance_count }
 *   remainingCredits — number (display only)
 *   onClose          — () => void (returns to the parent screen)
 *
 * The card grid shows ONE row per file with its icon, friendly label,
 * exact filename in mono, formatted size, and a status pill. The two
 * primary CTAs at the bottom are the "Download Package" zip and the
 * "Forward to your architect" pre-filled mailto.
 */
function formatBytes(n) {
  if (!n || n <= 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

const FILES = [
  {
    key: "floor_plan_pdf_bytes",
    Icon: FileText,
    label: "Architectural Floor Plan",
    suffix: "FLOOR_PLAN.PDF",
    blurb:
      "Clean, multi-page PDF with door swings, dimensions in feet-inches, and a stamped title block.",
    pillLabel: "PDF · ARCH",
  },
  {
    key: "ifc_bytes",
    Icon: Box,
    label: "BIM 4 File",
    suffix: "BIM.IFC",
    blurb:
      "Open-standard IFC 4. Imports natively into Revit, ArchiCAD, Vectorworks, BricsCAD, BlenderBIM.",
    pillLabel: "IFC · BIM-READY",
  },
  {
    key: "acceptance_log_pdf_bytes",
    Icon: ShieldCheck,
    label: "Acceptance Log",
    suffix: "ACCEPTANCE-LOG.PDF",
    blurb:
      "Time-stamped audit trail proving conceptual-deliverables acknowledgement. Hand to your architect.",
    pillLabel: "AUDIT",
  },
  {
    key: "readme",
    Icon: ScrollText,
    label: "Architect README",
    suffix: "README.TXT",
    blurb:
      "Plain-text handoff notice + file manifest. The first thing your architect reads.",
    pillLabel: "MANIFEST",
  },
];

export default function ProjectDeliveryDashboard({
  designName,
  designId,
  filename,
  zipBlobUrl,
  sizeBytes,
  contents,
  remainingCredits,
  onClose,
}) {
  const handleDownload = () => {
    if (!zipBlobUrl) {
      toast.error("Package no longer in memory — rebuild from your dashboard.");
      return;
    }
    const a = document.createElement("a");
    a.href = zipBlobUrl;
    a.download = filename || "architect-package.zip";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Architect package downloaded.");
  };

  const handleForward = () => {
    const subject = encodeURIComponent(
      `Conceptual design package — ${designName || designId}`,
    );
    const body = encodeURIComponent(
      [
        `Hi,`,
        ``,
        `Attached is the conceptual design package I generated for "${designName || designId}".`,
        ``,
        `It contains:`,
        `  • An architectural floor-plan PDF`,
        `  • An IFC 4 BIM file (Revit / ArchiCAD ready)`,
        `  • A time-stamped acceptance log`,
        `  • A README explaining what's inside`,
        ``,
        `These are CONCEPTUAL deliverables. I understand they require your professional review and stamp before permitting or construction.`,
        ``,
        `Generated with Blueprint AI · https://blueprintai.app`,
        `Project ID: ${designId}`,
      ].join("\n"),
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div
      className="bg-zinc-950 text-zinc-100"
      data-testid="project-delivery-dashboard"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="project-delivery-back-btn"
        className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-brass-400 transition-colors mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        // back to account
      </button>

      {/* Hero */}
      <section className="mb-10 border border-brass-500/40 bg-gradient-to-br from-brass-950/40 via-zinc-950 to-zinc-950 p-7">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 border border-brass-500/40 bg-brass-950/50 grid place-items-center shrink-0">
            <Package className="w-7 h-7 text-brass-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 mb-1 flex items-center gap-2">
              <Sparkles className="w-3 h-3" />
              Project Delivered
            </div>
            <h1
              data-testid="project-delivery-design-name"
              className="font-display text-2xl md:text-3xl text-zinc-50 mb-2 truncate"
            >
              {designName || designId}
            </h1>
            <p className="text-sm text-zinc-300 leading-relaxed max-w-2xl">
              Your conceptual design package is ready.{" "}
              <strong className="text-zinc-100">4 files</strong> · total{" "}
              <strong
                className="text-zinc-100"
                data-testid="project-delivery-total-size"
              >
                {formatBytes(sizeBytes)}
              </strong>
              {remainingCredits !== undefined && (
                <>
                  {" "}
                  ·{" "}
                  <span className="font-mono text-[10px] uppercase tracking-widest text-brass-300">
                    {remainingCredits} credits left
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
      </section>

      {/* File grid */}
      <section
        data-testid="project-delivery-files"
        className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10"
      >
        {FILES.map((f) => {
          const Icon = f.Icon;
          // README is fixed-size — derive from a tiny constant since the
          // server doesn't return its byte count (it's hand-written from a
          // template).
          const bytes =
            f.key === "readme" ? 800 : (contents || {})[f.key] || 0;
          return (
            <div
              key={f.key}
              data-testid={`project-delivery-file-${f.key}`}
              className="border border-zinc-800 bg-zinc-950 p-5 hover:border-brass-700/50 transition-colors group"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 border border-zinc-700 bg-zinc-900 grid place-items-center shrink-0 group-hover:border-brass-700/50 transition-colors">
                  <Icon className="w-5 h-5 text-brass-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <div className="font-display text-sm text-zinc-100 truncate">
                      {f.label}
                    </div>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-brass-400 px-1.5 py-0.5 border border-brass-700/40 bg-brass-950/30 shrink-0">
                      {f.pillLabel}
                    </span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 truncate">
                    {f.suffix} · {formatBytes(bytes)}
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    {f.blurb}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* Acceptance log highlight */}
      {(contents?.acceptance_count || 0) > 0 && (
        <div
          data-testid="project-delivery-acceptance-summary"
          className="mb-10 border-l-2 border-brass-500 pl-4 py-1 text-xs text-zinc-400"
        >
          Acceptance log includes{" "}
          <strong className="text-zinc-200">
            {contents.acceptance_count}
          </strong>{" "}
          time-stamped consent event
          {contents.acceptance_count === 1 ? "" : "s"} for this design.
        </div>
      )}

      {/* Primary CTAs */}
      <section className="flex flex-wrap items-center gap-3 mb-10">
        <button
          type="button"
          data-testid="project-delivery-download-btn"
          onClick={handleDownload}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-brass-600 hover:bg-brass-500 text-white font-mono text-[10px] uppercase tracking-widest transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          Download Package · ZIP
        </button>
        <button
          type="button"
          data-testid="project-delivery-forward-btn"
          onClick={handleForward}
          className="inline-flex items-center gap-2 px-5 py-2.5 border border-brass-700 text-brass-300 hover:bg-brass-950/50 font-mono text-[10px] uppercase tracking-widest transition-colors"
        >
          <Mail className="w-3.5 h-3.5" />
          Forward to your architect
        </button>
      </section>

      {/* Disclaimer footer */}
      <p className="text-[11px] text-zinc-500 leading-relaxed max-w-2xl border-t border-zinc-900 pt-5">
        These files are <strong className="text-zinc-300">conceptual
        deliverables</strong>. They require review and a stamp from a
        locally licensed architect or engineer before permitting or
        construction. The acceptance log is your provable record of that
        acknowledgement.
      </p>
    </div>
  );
}
